import React, { useMemo, useState } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import { formatHelper } from "helper/format";

const FREQUENCY_LABEL = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  HALF_YEARLY: "Semi-Annually",
  YEARLY: "Annually",
};

const BENEFITS = [
  "AI-driven trade ideas with precision–backed entry & exit points",
  "Real–time insights into directional bias, momentum alerts, and trend shifts",
  "Supports positions across all signals for strategic engagement in any market condition",
  "Helps manage exposure by avoiding low-probability trades and focusing on high-confidence setups",
  "Track institutional buying/selling patterns to align your trades with market movers.",
];

const FALLBACK_PLANS = [
  {
    planFrequency: "MONTHLY",
    planPrice: 25000,
    planCode: "JSE001",
    gstNote: "Incl. GST",
  },
  {
    planFrequency: "QUARTERLY",
    planPrice: 65000,
    planCode: "JSE002",
    gstNote: "Excl. GST",
  },
];

const CARD_GRADIENT = "linear-gradient(162deg, #4D5D8E 0%, #141929 100%)";
const BADGE_TEXT = "#4E63A4";
const CTA_TEXT = "#4E63A4";

const PeriodToggle = ({ value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "stretch",
      width: 172.5,
      height: 39.5,
      p: "3.75px",
      borderRadius: 999,
      bgcolor: "#F3F4F6",
      border: "1px solid #E5E7EB",
      boxShadow:
        "0 9px 22px -9px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      overflow: "hidden",
    }}
  >
    {["MONTHLY", "QUARTERLY"].map((period) => {
      const active = value === period;
      return (
        <Box
          key={period}
          component="button"
          type="button"
          onClick={() => onChange(period)}
          sx={{
            flex: 1,
            border: 0,
            cursor: "pointer",
            borderRadius: 999,
            bgcolor: active ? "#fff" : "transparent",
            boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
          }}
        >
          <Text
            sx={{
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "#111827" : "#6B7280",
              lineHeight: 1,
            }}
          >
            {FREQUENCY_LABEL[period]}
          </Text>
        </Box>
      );
    })}
  </Box>
);

const SentimentPricingCard = ({ plan, onUnlock, showFrequencyBadge = true }) => {
  const frequencyLabel =
    FREQUENCY_LABEL[plan.planFrequency] || plan.planFrequency || "";
  const gstNote =
    plan.gstNote ||
    (plan.planFrequency === "MONTHLY" ? "Incl. GST" : "Excl. GST");

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2.5,
        borderRadius: "20px",
        bgcolor: "#fff",
        border: "1px solid #E2E8F0",
        boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
      }}
    >
      <Stack
        spacing={4}
        sx={{
          p: 2.5,
          height: "100%",
          borderRadius: "16px",
          background: CARD_GRADIENT,
          boxShadow: "0 10px 12px rgba(37,99,235,0.18)",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Box sx={{ px: 2, py: 0.5, borderRadius: "14px", bgcolor: "#fff" }}>
              <Text sx={{ fontSize: 12, fontWeight: 500, color: BADGE_TEXT }}>
                F & O Trading
              </Text>
            </Box>
            {showFrequencyBadge ? (
              <Box sx={{ px: 2, py: 0.5, borderRadius: "14px", bgcolor: "#fff" }}>
                <Text sx={{ fontSize: 12, fontWeight: 500, color: BADGE_TEXT }}>
                  {frequencyLabel}
                </Text>
              </Box>
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Text sx={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              {formatHelper.currency(plan.planPrice)}
            </Text>
            <Text sx={{ fontSize: 12, color: "rgba(255,255,255,0.9)" }}>
              {gstNote}
            </Text>
          </Stack>

          <Button
            onClick={() => onUnlock?.(plan)}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />}
            sx={{
              width: "100%",
              height: 42,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
              color: CTA_TEXT,
              bgcolor: "#fff",
              boxShadow: "0 10px 12px rgba(59,130,246,0.2)",
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            Unlock
          </Button>
        </Stack>

        <Stack spacing={1.25}>
          {BENEFITS.map((benefit) => (
            <Stack
              key={benefit}
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  display: "grid",
                  placeItems: "center",
                  mt: "1px",
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 16, color: CTA_TEXT }} />
              </Box>
              <Text sx={{ fontSize: 14, color: "#fff", lineHeight: "21px" }}>
                {benefit}
              </Text>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

const SentimentPricingSection = ({ plans = [], onUnlock }) => {
  const [period, setPeriod] = useState("QUARTERLY");

  const displayPlans = useMemo(() => {
    const fromApi = (plans || []).filter(
      (p) =>
        p.planFrequency === "MONTHLY" || p.planFrequency === "QUARTERLY",
    );

    if (fromApi.length) {
      return [...fromApi]
        .map((p) => {
          const fallback =
            FALLBACK_PLANS.find((f) => f.planFrequency === p.planFrequency) ||
            FALLBACK_PLANS[0];
          return {
            ...fallback,
            ...p,
            planPrice: Number(p.planPrice ?? fallback.planPrice) || fallback.planPrice,
          };
        })
        .sort((a, b) => {
          const order = { MONTHLY: 0, QUARTERLY: 1 };
          return (order[a.planFrequency] ?? 9) - (order[b.planFrequency] ?? 9);
        });
    }

    return FALLBACK_PLANS;
  }, [plans]);

  const selectedPlan =
    displayPlans.find((p) => p.planFrequency === period) || displayPlans[0];

  return (
    <>
      {/* Desktop — both cards side by side */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "1fr 1fr",
          gap: 4,
          width: "100%",
        }}
      >
        {displayPlans.map((plan) => (
          <SentimentPricingCard
            key={plan.planCode || plan.planFrequency}
            plan={plan}
            onUnlock={onUnlock}
          />
        ))}
      </Box>

      {/* Mobile — Pricing header + Monthly/Quarterly toggle + one card */}
      <Stack
        spacing={2}
        sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            Pricing
          </Text>
          <PeriodToggle value={period} onChange={setPeriod} />
        </Stack>
        {selectedPlan ? (
          <SentimentPricingCard
            plan={selectedPlan}
            onUnlock={onUnlock}
            showFrequencyBadge={false}
          />
        ) : null}
      </Stack>
    </>
  );
};

export default SentimentPricingSection;
